import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import CustomButton from '../../../components/CustomButton';
import type { DropdownMenuItem } from '../../../components/DropdownMenu';
import RowActionsMenu from '../../../components/RowActionsMenu';
import InfoTooltip from '../../../components/InfoTooltip';
import TableCard, {
    TableHeaderRow,
    tableCellClass,
} from '../../../components/TableCard';
import { tenantSelector } from '../../../selector/authenticationSelector';
import {
    useArchiveCustomerClassMutation,
    useCreateCustomerClassMutation,
    useGetCustomerClassesQuery,
    useUpdateCustomerClassMutation,
} from '../../../api/organization/organizationApiSlice';
import { CustomerClass } from '../../../api/organization/organizationApiTypes';
import { formatDate } from '../../../utils';

/**
 * Customer classes — the segments a tenant's tariffs can distinguish (#1808).
 *
 * ## Why this tab exists
 *
 * The class table, its API and the org/unit assignment all shipped with H-3b
 * (#1583/#1721); only the management UI was deferred. The consequence was
 * invisible: every tenant in production carried exactly one class, the seeded
 * `Standard`, because nothing could create a second. `pricescale.customer_class_id`
 * would therefore have had a single value per tenant to point at, and the whole
 * tariff axis would have been inert.
 *
 * ## The system default is not editable away
 *
 * Exactly one `is_system_default` class per tenant is the terminal fallback of
 * the resolution chain, enforced by a partial unique index and seeded by
 * migration. The API refuses to archive it (409). The row is still renameable —
 * a tenant may well want to call it "Standardtarif" — so only the archive
 * action is withheld, and it says WHY rather than silently missing: a disabled
 * control with no explanation reads as a bug.
 */
const CustomerClassesTab: React.FC = () => {
    const { t, i18n } = useTranslation();
    const tenant = useSelector(tenantSelector);
    const language: 'de' | 'en' = i18n.language?.startsWith('de') ? 'de' : 'en';

    const { data, isLoading, isError, error } = useGetCustomerClassesQuery(undefined, {
        skip: !tenant,
    });

    const [createClass, { isLoading: isCreating }] = useCreateCustomerClassMutation();
    const [updateClass, { isLoading: isUpdating }] = useUpdateCustomerClassMutation();
    const [archiveClass] = useArchiveCustomerClassMutation();

    const [draftName, setDraftName] = useState('');
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editingName, setEditingName] = useState('');
    const [actionError, setActionError] = useState<string | null>(null);

    const classes: CustomerClass[] = useMemo(
        () => (data ?? []).filter((row) => row.archivedAt === null),
        [data],
    );

    /**
     * Show the server's own message where there is one. A 409 here is
     * meaningful — "this is the resolution fallback" — and collapsing it into a
     * generic failure costs the operator the reason (#2255/#2258).
     */
    const messageFrom = useCallback(
        (err: unknown, fallbackKey: string): string => {
            const serverMessage = (err as { data?: { message?: string } })?.data?.message;
            return serverMessage || t(fallbackKey);
        },
        [t],
    );

    const handleCreate = useCallback(async () => {
        const name = draftName.trim();
        if (!name) return;
        setActionError(null);
        try {
            await createClass({ name }).unwrap();
            setDraftName('');
        } catch (err) {
            setActionError(messageFrom(err, 'customer_class.errors.create'));
        }
    }, [createClass, draftName, messageFrom]);

    const handleRename = useCallback(async () => {
        const name = editingName.trim();
        if (!editingId || !name) return;
        setActionError(null);
        try {
            await updateClass({ id: editingId, name }).unwrap();
            setEditingId(null);
            setEditingName('');
        } catch (err) {
            setActionError(messageFrom(err, 'customer_class.errors.rename'));
        }
    }, [editingId, editingName, messageFrom, updateClass]);

    const handleArchive = useCallback(
        async (row: CustomerClass) => {
            setActionError(null);
            try {
                await archiveClass(row.id).unwrap();
            } catch (err) {
                setActionError(messageFrom(err, 'customer_class.errors.archive'));
            }
        },
        [archiveClass, messageFrom],
    );

    /**
     * Row actions. The system default carries no archive entry at all, rather
     * than a disabled one: the reason it cannot be archived is on the tooltip
     * beside its role, and a greyed item with no explanation reads as a broken
     * menu. Renaming stays available — "Standard" is a name a tenant may well
     * want to change.
     */
    const renameItem = useCallback(
        (row: CustomerClass): DropdownMenuItem => ({
            id: 'rename',
            label: t('customer_class.actions.rename'),
            onClick: () => {
                setEditingId(row.id);
                setEditingName(row.name);
            },
        }),
        [t],
    );
    const archiveItem = useCallback(
        (row: CustomerClass): DropdownMenuItem => ({
            id: 'archive',
            label: t('customer_class.actions.archive'),
            hidden: row.isSystemDefault,
            onClick: () => void handleArchive(row),
        }),
        [handleArchive, t],
    );

    let listContent: React.ReactNode;
    if (isError) {
        // A tenant on the legacy store gets a 409 from the API: customer
        // classes are an operational-schema concept and its database has no
        // such table. Saying so beats "could not load", which reads as an
        // outage.
        listContent = (
            <p className={'p-6 text-sm text-foreground/60'}>
                {messageFrom(error, 'customer_class.list.load_error')}
            </p>
        );
    } else if (isLoading || !tenant) {
        listContent = (
            <div className={'p-10 flex items-center justify-center'}>
                <div className={'animate-spin rounded-full h-10 w-10 border-b-2 border-primary'} />
            </div>
        );
    } else if (classes.length === 0) {
        listContent = (
            <p className={'p-6 text-sm text-foreground/60'}>{t('customer_class.list.empty')}</p>
        );
    } else {
        listContent = (
            <TableCard>
                <TableHeaderRow
                    labels={[
                        t('customer_class.list.columns.name'),
                        t('customer_class.list.columns.role'),
                        t('customer_class.list.columns.created'),
                        '',
                    ]}
                />
                <tbody>
                    {classes.map((row) => (
                        <tr key={row.id} className={'border-b border-foreground/5 last:border-0'}>
                            <td className={tableCellClass}>
                                {editingId === row.id ? (
                                    <div className={'flex items-center gap-2'}>
                                        <input
                                            className={
                                                'border border-foreground/20 rounded px-2 py-1 text-sm'
                                            }
                                            value={editingName}
                                            autoFocus={true}
                                            onChange={(e) => setEditingName(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') void handleRename();
                                                if (e.key === 'Escape') setEditingId(null);
                                            }}
                                            aria-label={t('customer_class.form.name')}
                                        />
                                        <CustomButton
                                            title={t('common.save')}
                                            onClick={handleRename}
                                            isFilled={true}
                                            disabled={isUpdating || !editingName.trim()}
                                        />
                                        <CustomButton
                                            title={t('common.cancel')}
                                            onClick={() => setEditingId(null)}
                                        />
                                    </div>
                                ) : (
                                    row.name
                                )}
                            </td>
                            <td className={tableCellClass}>
                                {row.isSystemDefault ? (
                                    <span className={'inline-flex items-center gap-1'}>
                                        {t('customer_class.list.system_default')}
                                        <InfoTooltip
                                            text={t('customer_class.help.system_default')}
                                        />
                                    </span>
                                ) : (
                                    t('common.not_applicable')
                                )}
                            </td>
                            <td className={tableCellClass}>
                                {formatDate(row.createdAt, language)}
                            </td>
                            <td className={`${tableCellClass} text-right`}>
                                {/* No detail page, so no row click; both actions
                                    change something and sit in the row's ⋮ menu,
                                    Archivieren last behind the divider
                                    (design-decisions.md › Zeilenaktionen in Tabellen). */}
                                <RowActionsMenu
                                    rowName={row.name}
                                    items={[renameItem(row)]}
                                    destructiveItems={[archiveItem(row)]}
                                />
                            </td>
                        </tr>
                    ))}
                </tbody>
            </TableCard>
        );
    }

    return (
        <div className={'flex flex-col gap-4'}>
            <p className={'text-sm text-foreground/60'}>{t('customer_class.intro')}</p>

            {!isError && (
                <div className={'flex items-end gap-2'}>
                    <div className={'flex flex-col gap-1'}>
                        <label
                            className={'text-sm text-foreground/60'}
                            htmlFor={'customer-class-name'}
                        >
                            {t('customer_class.form.name')}
                        </label>
                        <input
                            id={'customer-class-name'}
                            className={'border border-foreground/20 rounded px-3 py-2 text-sm'}
                            value={draftName}
                            placeholder={t('customer_class.form.placeholder')}
                            onChange={(e) => setDraftName(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') void handleCreate();
                            }}
                        />
                    </div>
                    <CustomButton
                        title={t('customer_class.actions.create')}
                        onClick={handleCreate}
                        isFilled={true}
                        disabled={isCreating || !draftName.trim() || !tenant}
                    />
                </div>
            )}

            {actionError && <p className={'text-sm text-primary'}>{actionError}</p>}

            {listContent}
        </div>
    );
};

export default CustomerClassesTab;
